import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Pagination,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { ADMIN_COOKIE_KEY } from '../../hooks/useAdmin';
import Cookies from 'js-cookie';

const API_URL = 'https://guide.zerdegroup.kz/api/';

interface Article {
  id: number;
  title: string;
  content: string;
  category_id: number;
  subcategory_id: number;
  author_id: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  author: {
    id: number;
    login: string;
    role: string;
  };
  category: {
    id: number;
    title: string;
  };
  subcategory: {
    id: number;
    title: string;
  };
}

const Articles: React.FC = () => {
  const navigate = useNavigate();
  const { userRole, userId, userFullAccess } = useAdmin();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        orderBy: 'updated_at',
        order: 'DESC'
      });

      // Если пользователь редактор без полного доступа, добавляем фильтр по автору
      if (userRole === 'editor' && userFullAccess === 0) {
        params.append('authorId', userId);
      }

      const response = await fetch(
        `${API_URL}/articles?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Ошибка при загрузке статей');
      }

      const data = await response.json();
      setArticles(data.items);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [page]);

  const handleEdit = (articleId: number) => {
    navigate(`/admin/panel/articles/edit/${articleId}`);
  };

  const handleView = (articleId: number) => {
    navigate(`/admin/panel/articles/view/${articleId}`);
  };

  const handleDelete = (articleId: number) => {
    setArticleToDelete(articleId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!articleToDelete) return;

    try {
      setLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);

      const response = await fetch(`${API_URL}/articles/${articleToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Ошибка при удалении статьи');
      }

      setDeleteDialogOpen(false);
      setArticleToDelete(null);
      fetchArticles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (isPublished: boolean) => {
    return isPublished ? 'success' : 'warning';
  };

  const getStatusLabel = (isPublished: boolean) => {
    return isPublished ? 'Опубликовано' : 'Черновик';
  };

  const canEditArticle = (article: Article) => {
    if (userRole === 'admin') return true;
    if (userRole === 'editor') {
      if (userFullAccess === 1) return true;
      return article.author_id === parseInt(userId);
    }
    return false;
  };

  const canDeleteArticle = (article: Article) => {
    if (userRole === 'admin') return true;
    if (userRole === 'editor') {
      // Редактор может удалять только свои статьи
      return article.author_id === parseInt(userId);
    }
    return false;
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.subcategory.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.author.login.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Статьи
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/panel/articles/create')}
        >
          Создать статью
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Поиск по названию, категории или автору..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Подкатегория</TableCell>
              <TableCell>Автор</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Последнее обновление</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredArticles.length > 0 ? (
              filteredArticles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>{article.title}</TableCell>
                  <TableCell>{article.category.title}</TableCell>
                  <TableCell>{article.subcategory.title}</TableCell>
                  <TableCell>
                    <Tooltip title={`Роль: ${article.author.role === 'admin' ? 'Администратор' : 'Редактор'}`}>
                      <Chip
                        label={article.author.login}
                        size="small"
                        color={article.author.role === 'admin' ? 'primary' : 'default'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(article.is_published)}
                      color={getStatusColor(article.is_published)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(article.updated_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Просмотр">
                      <IconButton
                        onClick={() => handleView(article.id)}
                        color="info"
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {canEditArticle(article) && (
                      <Tooltip title="Редактировать">
                        <IconButton
                          onClick={() => handleEdit(article.id)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteArticle(article) && (
                      <Tooltip title="Удалить">
                        <IconButton
                          onClick={() => handleDelete(article.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          Вы уверены, что хотите удалить эту статью? Это действие нельзя отменить.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Articles; 